/**
 * Rosenhart — Video bfcache fix + runtime source selection
 * (Sprint fix-bfcache-video + Sprint fix-home-hero-source-selection, 24 mai 2026)
 *
 * Couvre 2 patterns :
 * 1. **Generic bfcache fix** : toutes les <video autoplay> du site (5 surfaces
 *    backbone vidéo : home + BTS PDP + 3 hero pages page-level + galerie PDP).
 *    Quand l'utilisateur revient via back-forward ou navigation interne, le
 *    browser restaure un snapshot HTML où <video autoplay> est en état paused.
 *    L'autoplay native ne se redéclenche pas → écran blanc / poster figé.
 *    Solution : event listener `pageshow` filtré sur event.persisted === true
 *    + force video.play() sur toutes les videos paused.
 *
 * 2. **Home hero runtime source selection** : .rh-hero__video uniquement.
 *    Le pattern <source media="..."> fragile Safari (networkState=3
 *    NETWORK_NO_SOURCE après navigation) est remplacé par data-src-desktop
 *    + data-src-mobile attributes sur le <video>. Le JS choisit la source
 *    au runtime selon window.innerWidth, set video.src direct, .load() +
 *    .play(). Re-évaluation au load + pageshow (peu importe event.persisted)
 *    + resize (orientation iPhone landscape↔portrait).
 *
 * NO-OP safe : video.play() sur vidéo en cours = pas de régression. Promise
 * rejection ignorée (Safari peut bloquer play() si user-gesture requis, mais
 * en autoplay muted playsinline les conditions sont remplies).
 */
(function () {
  'use strict';

  var DESKTOP_BREAKPOINT = 768;

  // Mini-patch (24 mai 2026 QA Thomas iPhone Safari) : mémorise le dernier
  // breakpoint évalué. iOS Safari émet des resize events au scroll lors du
  // toggle de la barre URL/navigation (window.innerHeight varie). Sans cette
  // garde, selectHomeHeroSource déclencherait video.load() + video.play() en
  // boucle → flash de reload permanent visible. Reload effectif UNIQUEMENT
  // quand le breakpoint cross (mobile <768 ↔ desktop ≥768).
  var lastBreakpoint = null; // 'desktop' | 'mobile' | null (boot)

  /**
   * Pattern 2 — Home hero runtime source selection.
   * Sets video.src on .rh-hero__video based on current viewport width.
   * Forces load() + play() to bypass Safari bfcache snapshot stale state.
   */
  function selectHomeHeroSource() {
    var video = document.querySelector('.rh-hero__video');
    if (!video) return;
    var isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    var currentBreakpoint = isDesktop ? 'desktop' : 'mobile';

    // Skip reload if breakpoint hasn't changed (avoids flash on iOS Safari
    // scroll which fires resize events when URL bar toggles).
    if (lastBreakpoint === currentBreakpoint) {
      // Still ensure video plays if paused (covers bfcache restore for home).
      if (video.paused) {
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {});
        }
      }
      return;
    }

    lastBreakpoint = currentBreakpoint;
    var targetSrc = isDesktop
      ? video.getAttribute('data-src-desktop')
      : video.getAttribute('data-src-mobile');
    if (!targetSrc) return;
    if (video.currentSrc !== targetSrc) {
      video.src = targetSrc;
      video.load();
    }
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {
        // Ignore rejection (user-gesture required, low power mode, etc.)
      });
    }
  }

  /**
   * Pattern 1 — Generic bfcache fix for all autoplay videos.
   * Triggers on pageshow event.persisted === true (page restored from bfcache).
   */
  function restartAutoplayVideos(event) {
    if (!event.persisted) return;
    var videos = document.querySelectorAll('video[autoplay]');
    videos.forEach(function (video) {
      if (video.paused) {
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {
            // Ignore rejection.
          });
        }
      }
    });
  }

  // Boot: select home hero source as soon as DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', selectHomeHeroSource);
  } else {
    selectHomeHeroSource();
  }

  // pageshow fires on initial load AND on bfcache restore.
  // We call both functions: home hero source selection (always) +
  // generic bfcache fix (only when event.persisted === true).
  window.addEventListener('pageshow', function (event) {
    selectHomeHeroSource();
    restartAutoplayVideos(event);
  });

  // Resize: re-evaluate home hero source if viewport crosses breakpoint
  // (orientation change iPhone landscape↔portrait, browser window resize Mac).
  var resizeTimeout = null;
  window.addEventListener('resize', function () {
    if (resizeTimeout) clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(selectHomeHeroSource, 200);
  });
})();
